import type { ChangeEvent, ComponentProps } from "react";

import { cn } from "../lib/cn";
import { fieldBase } from "./Input";

export interface TextRules {
  /** One character that may be typed, e.g. /[a-z0-9-]/. Everything else is dropped as it is typed or pasted. */
  allow: RegExp;
  /** Change letters to upper or lower case as they are typed. */
  letterCase?: "upper" | "lower";
  /** Turn spaces into this character first (slugs: "-"). */
  spaceTo?: string;
  /** Characters that may not start the value and may not repeat next to each other (slugs: "-"). */
  separator?: string;
}

export function sanitizeText(
  raw: string,
  { allow, letterCase, spaceTo, separator }: TextRules,
): string {
  let text =
    letterCase === "upper" ? raw.toUpperCase() : letterCase === "lower" ? raw.toLowerCase() : raw;
  if (spaceTo !== undefined) text = text.replace(/\s+/g, spaceTo);
  const single = new RegExp(`^(?:${allow.source})$`, allow.flags.replace("g", ""));
  text = Array.from(text)
    .filter((char) => single.test(char))
    .join("");
  if (separator) {
    const sep = separator.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    text = text
      .replace(new RegExp(`${sep}{2,}`, "g"), separator)
      .replace(new RegExp(`^${sep}+`), "");
  }
  return text;
}

/** Ready-made rules for the codes this marketplace uses (the server checks the full format). */
export const textRules = {
  /** URL slugs: lower-case letters, digits and single dashes ("royal-enfield-classic-350"). */
  slug: { allow: /[a-z0-9-]/, letterCase: "lower", spaceTo: "-", separator: "-" },
  /** Attribute codes: lower-case letters, digits and underscores ("engine_cc"). */
  code: { allow: /[a-z0-9_]/, letterCase: "lower", spaceTo: "_", separator: "_" },
  /** Coupon codes: capital letters, digits and dashes ("DIWALI-500"). */
  couponCode: { allow: /[A-Z0-9-]/, letterCase: "upper" },
  /** Stock-keeping units: letters, digits and . _ / - ("BRK-PAD/FR.01"). */
  sku: { allow: /[A-Za-z0-9._/-]/ },
  /** GSTIN, PAN and IFSC: capital letters and digits. */
  taxId: { allow: /[A-Z0-9]/, letterCase: "upper" },
  /** Vehicle registration numbers: capital letters and digits ("MH12AB1234"). */
  registration: { allow: /[A-Z0-9]/, letterCase: "upper" },
  /** Digits only, kept as text (PIN codes, bank account numbers, HSN codes, OTPs). */
  digits: { allow: /\d/ },
  /** Phone numbers: digits, a leading +, spaces and dashes as typed. */
  phone: { allow: /[\d+\s-]/ },
} satisfies Record<string, TextRules>;

export interface PatternInputProps extends Omit<ComponentProps<"input">, "type">, TextRules {}

/**
 * A text box that only accepts the characters of a format (slugs, codes, SKUs, tax ids, PIN codes): the rest never
 * appears, whether typed or pasted, and letters change case as you type. Works with `register` and controlled.
 */
export function PatternInput({
  allow,
  letterCase,
  spaceTo,
  separator,
  className,
  onChange,
  ...props
}: PatternInputProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const clean = sanitizeText(input.value, { allow, letterCase, spaceTo, separator });
    if (clean !== input.value) {
      const caret =
        (input.selectionStart ?? input.value.length) - (input.value.length - clean.length);
      input.value = clean;
      input.setSelectionRange?.(Math.max(caret, 0), Math.max(caret, 0));
    }
    onChange?.(event);
  };
  return (
    <input
      type="text"
      autoComplete="off"
      autoCapitalize={letterCase === "upper" ? "characters" : "none"}
      spellCheck={false}
      className={cn(fieldBase, "h-11", className)}
      onChange={handleChange}
      {...props}
    />
  );
}
